import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Input from '../Input';
import { useTheme } from '../../context/ThemeContext';
import axios from 'axios';
import { CloseOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import Message from '../Message';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding-top: 80px;

  @media (max-width: 768px) {
    padding-top: 140px;
    align-items: flex-start;
    overflow-y: auto;
  }
`;

const ModalWrapper = styled.div`
  position: relative;
  width: 447px;

  @media (max-width: 768px) {
    width: 100%;
    display: flex;
    justify-content: center;
  }
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.cardBg};
  border-radius: 16px;
  padding: 32px 24px;
  width: 100%;
  max-width: 447px;
  position: relative;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin: 16px;
  box-sizing: border-box;
  overflow: hidden;

  @media (max-width: 768px) {
    width: calc(100% - 32px);
    margin: 0 16px;
    height: auto;
    min-height: 445px;
  }

  @media (max-width: 480px) {
    padding: 24px 16px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 24px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.text};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  
  &:hover {
    opacity: 0.8;
  }
`;

const Title = styled.h2`
  text-align: left;
  margin: 0 0 32px;
  color: ${({ theme }) => theme.text};
  font-size: 20px;
  font-weight: 500;
  padding-left: 8px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const StyledInput = styled(Input)`
  width: 100%;
  height: 44px;
  border-radius: 22px;
  border: none;
  background: #E8E8E8;
  padding: 0 20px;
  box-sizing: border-box;
  
  input {
    height: 44px;
    background-color: #E8E8E8 !important;
    color: #000000;
    font-size: 14px;
    width: 100%;
    box-sizing: border-box;
    border: none;
    transition: all 0.3s ease;
    outline: none;
    line-height: 44px;
    padding: 0;
    
    &::placeholder {
      color: #888888;
    }

    &:hover {
      background-color: #E8E8E8 !important;
    }

    &:focus {
      background-color: #E8E8E8 !important;
      outline: none;
    }

    /* 处理浏览器自动填充的背景色 - 强化版 */
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
      -webkit-box-shadow: 0 0 0 1000px #E8E8E8 inset !important;
      box-shadow: 0 0 0 1000px #E8E8E8 inset !important;
      -webkit-text-fill-color: #000000 !important;
      caret-color: #000000;
      transition: background-color 5000s ease-in-out 0s, color 5000s ease-in-out 0s;
      background-color: #E8E8E8 !important;
    }
  }

  &:focus-within {
    border: 1px solid #FF4D4F;
    background: #E8E8E8 !important;
    outline: none;
    
    input {
      background-color: #E8E8E8 !important;
      outline: none;
    }
  }

  /* 移除所有蓝色阴影 */
  &,
  .ant-input,
  .ant-input-affix-wrapper,
  .ant-input-affix-wrapper-focused {
    box-shadow: none !important;
    outline: none !important;
    background-color: #E8E8E8 !important;
    color: #000000 !important;
  }

  .ant-input-affix-wrapper:focus,
  .ant-input-affix-wrapper-focused,
  .ant-input-affix-wrapper:hover,
  .ant-input:focus,
  .ant-input:hover {
    box-shadow: none !important;
    outline: none !important;
    background-color: #E8E8E8 !important;
    color: #000000 !important;
  }
  
  /* 添加更强的CSS规则覆盖Chrome的自动填充样式 */
  @-webkit-keyframes autofill {
    to {
      color: #000000;
      background: #E8E8E8;
    }
  }
  
  input:-webkit-autofill {
    -webkit-animation-name: autofill;
    -webkit-animation-fill-mode: both;
  }
  
  /* 额外的CSS规则来防止浏览器自动填充样式 */
  input:-internal-autofill-selected {
    background-color: #E8E8E8 !important;
    background-image: none !important;
    color: #000000 !important;
  }
  
  /* 通用样式覆盖 */
  @-webkit-keyframes onAutoFillStart { from {} to {}}
  @keyframes onAutoFillStart { from {} to {}}
  
  input:-webkit-autofill {
    animation-name: onAutoFillStart;
    animation-fill-mode: both;
  }
  
  input:autofill {
    background-color: #E8E8E8 !important;
    color: #000000 !important;
  }

  /* 额外覆盖Ant Design内部input元素 */
  .ant-input-affix-wrapper > input.ant-input {
    color: #000000 !important;
    background-color: #E8E8E8 !important;
  }
  
  .ant-input-affix-wrapper .ant-input-suffix {
    color: #000000 !important;
  }
`;

const PasswordInput = styled(StyledInput)`
  .ant-input-suffix {
    color: rgba(0, 0, 0, 0.85);
    cursor: pointer;
    margin-right: 12px;
    font-size: 18px;
    display: flex;
    align-items: center;
    
    &:hover {
      color: rgba(0, 0, 0, 0.65);
    }

    .anticon {
      color: rgba(0, 0, 0, 0.85);
      
      &:hover {
        color: rgba(0, 0, 0, 0.65);
      }
    }
  }
`;

const CaptchaWrapper = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  width: 100%;
`;

const CaptchaInputWrapper = styled.div`
  flex: 1;
`;

const CaptchaDisplay = styled.div`
  width: 100px;
  height: 44px;
  border-radius: 22px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.inputBg || theme.cardBg};
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.border};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    will-change: transform;
    transform: translateZ(0);
    backface-visibility: hidden;
    -webkit-font-smoothing: subpixel-antialiased;
    image-rendering: -webkit-optimize-contrast;
  }

  div {
    color: ${({ theme }) => theme.text};
  }
`;

const VerificationCodeWrapper = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  width: 100%;
`;

const SendButton = styled.button`
  height: 44px;
  padding: 0 20px;
  border-radius: 22px;
  border: none;
  background: #FF4D4F;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  
  &:hover {
    background: #E53E3E;
  }
  
  &:disabled {
    background: #FFB2B3;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  height: 44px;
  border-radius: 22px;
  border: none;
  background: ${({ $isDark }) => $isDark ? '#FF4D4F' : '#FF4D4F'};
  color: white;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  box-sizing: border-box;
  margin-top: 8px;
  
  &:hover {
    background: #E53E3E;
  }
  
  &:disabled {
    background: #FFB2B3;
    cursor: not-allowed;
  }
`;

const ResetPasswordModalMessage = styled.div.attrs({ id: 'reset-password-modal-message' })`
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2000;
  pointer-events: none;
  display: flex;
  justify-content: center;
  
  .ant-message {
    position: static;
    display: flex;
    justify-content: center;
  }

  .ant-message-notice {
    padding: 0;
    margin: 0;
    display: flex;
    justify-content: center;
  }

  .ant-message-notice-content {
    display: inline-block;
    background: rgba(0, 0, 0, 0.85);
    color: white;
    border-radius: 4px;
    padding: 10px 16px;
    box-shadow: 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08);
    white-space: nowrap;
  }

  @media (max-width: 768px) {
    width: calc(100% - 32px);
    top: 16px;
    padding: 0 16px;
    box-sizing: border-box;
  }
`;

const ResetPasswordModal = ({ onClose }) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    smsCode: '',
    captcha: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [captchaImage, setCaptchaImage] = useState('');
  const [countdown, setCountdown] = useState(0);
  const captchaInitializedRef = useRef(false);
  const [rid, setRid] = useState('');

  const showMessage = (content, type) => {
    Message[type](content, 'reset-password-modal-message');
  };

  const fetchCaptcha = async () => {
    try {
      // 生成随机数作为rid
      const randomId = Math.floor(Math.random() * 1000000000).toString();
      setRid(randomId);
      
      const timestamp = new Date().getTime(); // 添加时间戳防止缓存
      const response = await axios.get(`//server.battleverse.cn/pub/captcha.do?t=${timestamp}&rid=${randomId}`);
      if (response.data.success) {
        setCaptchaImage(`data:image/jpeg;base64,${response.data.data.captcha}`);
      }
    } catch (err) {
      showMessage('获取验证码失败，请刷新重试', 'error');
    }
  };

  useEffect(() => {
    if (!captchaInitializedRef.current) {
      captchaInitializedRef.current = true;
      fetchCaptcha();
    }
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSendSmsCode = async () => {
    if (!formData.phoneNumber.match(/^1\d{10}$/)) {
      showMessage('请输入正确的手机号码', 'error');
      return;
    }

    if (!rid) {
      const randomId = Math.floor(Math.random() * 1000000000).toString();
      setRid(randomId);
    }

    try {
      const response = await axios.post('//server.battleverse.cn/pub/retrieve-send-msg.do', {
        phoneNumber: formData.phoneNumber,
        captcha: formData.captcha,
        rid: rid
      });

      if (response.data.success) {
        showMessage('验证码发送成功，请注意查收', 'success');
        setCountdown(60);
      } else {
        showMessage(response.data.msg || '发送验证码失败', 'error');
        fetchCaptcha();
      }
    } catch (err) {
      showMessage('发送验证码失败，请稍后重试', 'error');
      fetchCaptcha();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (formData.password.length < 6) {
      showMessage('密码不能少于6位', 'error');
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      showMessage('两次输入的密码不一致', 'error');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post('//server.battleverse.cn/pub/retrieve-password.do', {
        password: formData.password,
        phoneNumber: formData.phoneNumber,
        smsCode: formData.smsCode,
        rid: rid
      });

      if (response.data.success) {
        showMessage('密码重置成功，请使用新密码登录', 'success');
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        showMessage(response.data.msg || '重置密码失败，请重试', 'error');
      }
    } catch (err) {
      showMessage('重置密码失败，请稍后重试', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalOverlay>
      <ResetPasswordModalMessage />
      <ModalWrapper>
        <ModalContent onClick={e => e.stopPropagation()}>
          <CloseButton onClick={onClose}>
            <CloseOutlined />
          </CloseButton>
          <Title>找回密码</Title>
          <Form onSubmit={handleSubmit}>
            <StyledInput
              type="tel"
              name="phoneNumber"
              placeholder="请输入手机号"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              required
            />
            <CaptchaWrapper>
              <CaptchaInputWrapper>
                <StyledInput
                  type="text"
                  name="captcha"
                  placeholder="请输入验证码"
                  value={formData.captcha}
                  onChange={handleInputChange}
                  required
                />
              </CaptchaInputWrapper>
              <CaptchaDisplay onClick={fetchCaptcha}>
                {captchaImage ? (
                  <img src={captchaImage} alt="验证码" />
                ) : (
                  <div>加载中...</div>
                )}
              </CaptchaDisplay>
            </CaptchaWrapper>
            <VerificationCodeWrapper>
              <StyledInput
                type="text"
                name="smsCode"
                placeholder="请输入短信验证码"
                value={formData.smsCode}
                onChange={handleInputChange}
                required
              />
              <SendButton
                type="button"
                onClick={handleSendSmsCode}
                disabled={countdown > 0}
              >
                {countdown > 0 ? `${countdown}s` : '发送验证码'}
              </SendButton>
            </VerificationCodeWrapper>
            <PasswordInput
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="请输入新密码（不少于6位）"
              value={formData.password}
              onChange={handleInputChange}
              required
              suffix={
                showPassword ?
                  <EyeOutlined onClick={() => setShowPassword(false)} style={{ color: 'black' }} /> :
                  <EyeInvisibleOutlined onClick={() => setShowPassword(true)} style={{ color: 'black' }} />
              }
            />
            <PasswordInput
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              placeholder="请确认新密码"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              required
              suffix={
                showConfirmPassword ?
                  <EyeOutlined onClick={() => setShowConfirmPassword(false)} style={{ color: 'black' }} /> :
                  <EyeInvisibleOutlined onClick={() => setShowConfirmPassword(true)} style={{ color: 'black' }} />
              }
            />
            <SubmitButton type="submit" disabled={loading} $isDark={isDarkMode}>
              {loading ? '提交中...' : '重置密码'}
            </SubmitButton>
          </Form>
        </ModalContent>
      </ModalWrapper>
    </ModalOverlay>
  );
};

export default ResetPasswordModal;
